// src/hooks/canvas/useTabSwitch.js
//
// Tab degisiminde sahne gecisi + kamera durumu kaydet/geri yukle.

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import { computeSafeSceneBox } from '../../../modules/fpSync';

export default function useTabSwitch(
  threeState, setThreeState, scenesRef, activeSceneRef,
  controlsRef, canvasRef, onSceneReady
) {
  const activeTabId = useSelector((state) => state.projectTabs.activeTabId);
  const activeTabIdRef = useRef(activeTabId);
  const tabCameraStatesRef = useRef({});
  const prevTabIdRef = useRef('mimari');

  useEffect(() => { activeTabIdRef.current = activeTabId; }, [activeTabId]);

  // Sayfa gorunur oldugunda aktif sahneyi senkronize et
  useEffect(() => {
    const syncScene = () => {
      if (document.hidden) return;
      const currentTab = activeTabIdRef.current;
      const expectedScene = scenesRef.current[currentTab];
      if (expectedScene && activeSceneRef.current !== expectedScene) {
        activeSceneRef.current = expectedScene;
        setThreeState(prev => prev ? { ...prev, scene: expectedScene } : prev);
        onSceneReady?.(expectedScene);
      }
    };
    document.addEventListener('visibilitychange', syncScene);
    window.addEventListener('focus', syncScene);
    return () => {
      document.removeEventListener('visibilitychange', syncScene);
      window.removeEventListener('focus', syncScene);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab degisimi
  useEffect(() => {
    if (!threeState) return;
    const { camera } = threeState;
    const controls = controlsRef.current;

    // Onceki tab'in kamera durumunu kaydet
    const prevTab = prevTabIdRef.current;
    if (prevTab && camera && controls) {
      tabCameraStatesRef.current[prevTab] = {
        px: camera.position.x, py: camera.position.y, pz: camera.position.z,
        zoom: camera.zoom,
        tx: controls.target.x, ty: controls.target.y, tz: controls.target.z,
      };
    }

    // Lazy: tab icin sahne yoksa olustur
    if (!scenesRef.current[activeTabId]) {
      scenesRef.current[activeTabId] = new THREE.Scene();
    }
    const nextScene = scenesRef.current[activeTabId];
    activeSceneRef.current = nextScene;

    // Kamera durumunu geri yukle veya sahneye fit et
    const saved = tabCameraStatesRef.current[activeTabId];
    if (saved && camera && controls) {
      camera.position.set(saved.px, saved.py, saved.pz);
      camera.zoom = saved.zoom;
      controls.target.set(saved.tx, saved.ty, saved.tz);
      camera.updateProjectionMatrix();
    } else if (camera && controls && activeTabId !== 'mimari') {
      const box = new THREE.Box3();
      let hasContent = false;
      nextScene.traverse((child) => {
        if (child.isMesh || child.isLine || child.isLineSegments) {
          box.expandByObject(child);
          hasContent = true;
        }
      });
      if (hasContent && !box.isEmpty()) {
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        camera.position.set(center.x, center.y, 10);
        controls.target.set(center.x, center.y, 0);
        const container = canvasRef.current;
        if (container) {
          const aspect = container.clientWidth / container.clientHeight;
          const contentAspect = size.x / (size.y || 1);
          const fitZoom = (contentAspect > aspect)
            ? container.clientWidth / size.x
            : container.clientHeight / size.y;
          camera.zoom = fitZoom * 0.9;
        }
        camera.updateProjectionMatrix();
      }
    }

    prevTabIdRef.current = activeTabId;
    setThreeState((prev) => ({ ...prev, scene: nextScene }));
    onSceneReady?.(nextScene);
  }, [activeTabId, !!threeState]); // eslint-disable-line react-hooks/exhaustive-deps

  return { activeTabId };
}
